import { BdArrayUtil } from '@bindoc-projects/util-domain-lib';
import type {
  FilterConditionPluginSpecification,
} from '../filter.plugin';
import {
  FilterConditionPluginImpl
} from '../filter.plugin';
import type { FilterConditionPlugin } from '../filter';
import type {
  BdCondition,
} from '@bindoc-projects/condition-domain-lib';
import {
  BdContentCondition,
} from '@bindoc-projects/condition-domain-lib';
import type {
  AysNrwServiceGroupId,
} from '@bindoc-projects/virtual-clinic-domain-lib';
import {
  AYS_NRW_SERVICE_GROUPS_DISPLAY_NAMES
} from '@bindoc-projects/virtual-clinic-domain-lib';
import type { FilterDetailedText } from '../filter-detailed-text';

export const BD_NRW_SERVICE_AREA_CONDITION_ID = 'nrwServiceArea';
export const FilterConditionNrwServiceAreaIdsPluginType: FilterConditionPluginSpecification<
  AysNrwServiceGroupId[],
  FilterConditionNrwServiceAreaIdsDto,
  BdNrwServiceAreaCondition
> = {
  type: 'nrwServiceArea',
  category: 'case',
  displayName: { de: 'NRW Leistungsbereiche' },
  sortingIndex: 6,
  occurrence: 'single',
  fromObject(
    obj: FilterConditionNrwServiceAreaIdsDto,
  ): FilterConditionPluginImpl<AysNrwServiceGroupId[]> {
    return new FilterConditionNrwServiceAreaIdsPlugin(
        obj.nrwServiceGroupIds as AysNrwServiceGroupId[],
      );
  },
  fromCondition(
    jsonObj: BdNrwServiceAreaCondition,
  ): FilterConditionPluginImpl<AysNrwServiceGroupId[]> {
    return new FilterConditionNrwServiceAreaIdsPlugin(
        jsonObj.includes as AysNrwServiceGroupId[],
      );
  },
  newInstance(): FilterConditionPluginImpl<AysNrwServiceGroupId[]> {
    return new FilterConditionNrwServiceAreaIdsPlugin([]);
  },
};

export class FilterConditionNrwServiceAreaIdsPlugin extends FilterConditionPluginImpl<
  AysNrwServiceGroupId[],
  FilterConditionNrwServiceAreaIdsDto
> {
  public static override Specification: FilterConditionPluginSpecification<
    AysNrwServiceGroupId[],
    FilterConditionNrwServiceAreaIdsDto,
    BdNrwServiceAreaCondition
  > = FilterConditionNrwServiceAreaIdsPluginType;

  private nrwServiceGroupIds: AysNrwServiceGroupId[];

  constructor(nrwServiceGroupIds: AysNrwServiceGroupId[]) {
    super(FilterConditionNrwServiceAreaIdsPluginType.type);
    // JSON round-trips can re-type integer-shaped ids ("1") as numbers, which
    // then break String#includes calls downstream. Normalise on the way in.
    this.nrwServiceGroupIds = (nrwServiceGroupIds ?? []).map(
      (id) => String(id) as AysNrwServiceGroupId,
    );
  }

  clone(): FilterConditionPlugin<AysNrwServiceGroupId[]> {
    return new FilterConditionNrwServiceAreaIdsPlugin([
      ...this.nrwServiceGroupIds,
    ]);
  }

  override equals(other: FilterConditionNrwServiceAreaIdsPlugin): boolean {
    return (
      super.equals(other) &&
      BdArrayUtil.equals(this.nrwServiceGroupIds, other.nrwServiceGroupIds)
    );
  }

  override validationToText(): string {
    if (this.nrwServiceGroupIds.length === 0) {
      return 'Mindestens einen NRW Leistungsbereich auswählen';
    } else {
      return '';
    }
  }

  toCondition(): BdCondition | null {
    return new BdNrwServiceAreaCondition(this.nrwServiceGroupIds, []);
  }

  toJSON(): FilterConditionNrwServiceAreaIdsDto | null {
    return {
      type: this.type,
      nrwServiceGroupIds: this.nrwServiceGroupIds,
    };
  }

  toText(): string | null {
    return this.nrwServiceGroupIds.length === 1
      ? 'ein NRW Leistungsbereich'
      : `${this.nrwServiceGroupIds.length} NRW Leistungsbereiche`;
  }

  override toDetailedText(): FilterDetailedText | null {
    const title = this.toText();
    if (!title) return null;
    const items = this.nrwServiceGroupIds.map((id) => {
      const displayName = AYS_NRW_SERVICE_GROUPS_DISPLAY_NAMES[id];
      return displayName ? `${id} — ${displayName}` : id;
    });
    return { title, items };
  }

  toValue(): AysNrwServiceGroupId[] {
    return this.nrwServiceGroupIds;
  }
}

export class BdNrwServiceAreaCondition extends BdContentCondition<string> {
  constructor(includes: string[], excludes: string[] = []) {
    super(BD_NRW_SERVICE_AREA_CONDITION_ID, includes, excludes);
  }
}

interface FilterConditionNrwServiceAreaIdsDto {
  type: string;
  nrwServiceGroupIds: string[];
}
